import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Study, StudySettingsV1 } from '@/model/Study.model.ts';
import Header from '@/components/UI/Header';
import { AnimalApiId } from '@/model/Animal.model.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { _isEmptyString, _isNil, _notNil } from '@/littledash.ts';
import { ActiveVariablesResponseV1 } from '@/model/Preset.model.ts';
import { useForm, Controller } from 'react-hook-form@latest';
import Button from '@/components/UI/Button';
import {
  generateTargetPresetData,
  MeasurementMapFormContext,
  MeasurementMapFormData,
  MeasurementOutputNode,
} from '@/components/Studies/Templates/CreateDataTable/steps/MapMeasurements.utils.tsx';
import Select from '@/components/UI/Select';
import Loading from '@/components/Loading';
import {
  addToPresetOption,
  createPayload,
  dropMeasurementOption,
  generateDefaultValues,
  generateSourcePresetData,
  ListItem,
  ListItemDependency,
  targetContainsSlugMessage,
  targetDoesNotContainsSlugMessage,
  useMapMeasurementsResolver,
  validateDependencies,
} from '@/components/Modals/MoveToStudy/PresetMapping/PresetMapping.utils.tsx';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook.ts';
import { Cage } from '@/model/Cage.model.ts';
import { SubmitHandler } from 'react-hook-form@latest';
import { useModalAction } from '@/utils/modal.tsx';
import { closeModal } from '@/components/Shared/methods.ts';
import Banner from '@/components/UI/Banner';
import { errorToast } from '@/helpers.tsx';
import { RouteService } from '@/support/RouteService';

interface PresetMappingActiveVariablesProps {
  sourceStudy: Study;
  targetStudy: Study;
  animalApiIds: Array<AnimalApiId>;
  sourceSettings: StudySettingsV1;
  targetSettings: StudySettingsV1;
  duplicateCageNames: boolean;
}

const PresetMappingActiveVariables: FC<PresetMappingActiveVariablesProps> = ({
  sourceStudy,
  targetStudy,
  sourceSettings,
  targetSettings,
  animalApiIds,
  duplicateCageNames,
}) => {
  const { openModal } = useModalAction();

  const targetPresetData = useMemo(
    () => generateTargetPresetData(targetSettings?.calculations ?? []),
    [targetSettings]
  );
  const sourcePresetData = useMemo(
    () => generateSourcePresetData(sourceSettings?.calculations ?? [], targetPresetData),
    [sourceSettings]
  );
  const [targetSlugUsedCount, updateTargetSlugUsedCount] = useState(new Map<string, number>());
  const defaultValues = useCallback(
    () =>
      generateDefaultValues(
        [...sourcePresetData.outputs.values(), ...sourcePresetData.inputs.values()],
        targetPresetData.outputFuse,
        targetPresetData.options
      ),
    []
  );
  const resolver = useMapMeasurementsResolver(
    useCallback(
      (value: Map<string, number>) => {
        updateTargetSlugUsedCount((prevState) => {
          if (value.size !== prevState.size) {
            return value;
          }
          for (const [slug, count] of value) {
            if (prevState.get(slug) !== count) {
              return value;
            }
          }
          return prevState;
        });
      },
      [updateTargetSlugUsedCount]
    )
  );

  const formMethods = useForm<MeasurementMapFormData, MeasurementMapFormContext>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver,
    context: {
      targetOptions: targetPresetData.options,
      sourceOutputs: sourcePresetData.outputs,
      sourceInputs: sourcePresetData.inputs,
    },
    defaultValues: defaultValues,
  });

  const handleOutputUpdate = useCallback(
    (node: MeasurementOutputNode, previousValue: string | null, value: string | null) => {
      if (previousValue === value) {
        return;
      }
      if (node?.dependencyParents?.length > 0) {
        node.dependencyParents.forEach((parent) => {
          formMethods.setValue(parent, null, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        });
      }
      if (node.children.size > 0) {
        if (_isNil(value)) {
          // Clear out inputs
          node.children.forEach((inputNode) => {
            formMethods.setValue(inputNode.slug, null, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
          });
        } else {
          // Update inputs with new suggestions
          node.children.forEach((inputNode) => {
            formMethods.setValue(
              inputNode.slug,
              targetPresetData.options
                .get(value ?? '')
                ?.fuse?.search(inputNode.search)
                ?.at(0)?.item?.slug ?? null,
              {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              }
            );
          });
        }
      }
    },
    [formMethods, targetPresetData.options]
  );

  const onSubmit: SubmitHandler<MeasurementMapFormData> = (data: MeasurementMapFormData) => {
    const { payload, measurementsToBeDropped, measurementsToBeAdded } = createPayload(
      data,
      sourceStudy.api_id,
      targetStudy.api_id,
      animalApiIds,
      sourcePresetData
    );
    openModal('PRESET_MAPPING_CONFIRMATION', {
      presetMappingPayload: payload,
      measurementsToBeAdded: measurementsToBeAdded,
      measurementsToBeDropped: measurementsToBeDropped,
      duplicateCageNames: duplicateCageNames,
      targetStudyId: targetStudy.id,
      sourceStudyId: sourceStudy.id,
      closeModal: closeModal,
    });
  };

  return (
    <>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} data-test-component="PresetMapping" data-test-element="form">
        <div className="ph4 pv3">
          <div className="pb3">
            <Header mainHeaderText="Move to another study" />
            <p className="b--moon-gray">Map measurements</p>
          </div>
          <Banner info className="w-50 mb2" dismiss={false} center>
            <p className="f6 near-black lh-title pb1 fw7">
              Double-check mappings to avoid deleting or invalidating data. This action cannot be undone.
            </p>
            <p className="f6 near-black lh-title">
              If you drop a measurement, any existing data from the old study will be deleted. If a calculation depends
              on unmapped or mismatched data, it cannot be imported into the new study.
            </p>
            <a
              href={RouteService.help('moving_animals_different_preset')}
              target="_blank"
              rel="noopener noreferrer"
              className="dib link blue f6"
            >
              Read more.
            </a>
          </Banner>
          <div className="ui-card">
            <div className="flex b--moon-gray pl4 pt3 pb3">
              <PresetHeader preset={sourceSettings} label="Previous Study Preset" rowWidth={30} />
              <PresetHeader preset={targetSettings} label="New Study Preset" rowWidth={20} />
            </div>
            <div className="pt2 pb2 pl4">
              {Array.from(sourcePresetData.outputs.values()).map((outputNode) => (
                <div
                  className="pb4 pt4 bt"
                  key={outputNode.slug}
                  data-test-element="output-container"
                  data-test-key={outputNode.slug}
                >
                  <div className="flex">
                    <div className="w-30 mb0">
                      <label>{outputNode.label}</label>
                      <label className="dark-gray">{outputNode.slug}</label>
                    </div>
                    <div className="flex w-70" data-test-element="output-target-slug">
                      <Controller
                        control={formMethods.control}
                        name={outputNode.slug}
                        render={({ field, fieldState }) => {
                          const outputValue = targetPresetData.options.get(field.value ?? '');
                          if (!outputNode.calculatedMeasurement) {
                            return (
                              <>
                                <div className="w-30">
                                  <Select
                                    name={outputNode.slug}
                                    className="w-100 pr2"
                                    value={outputValue?.slug === 'addToPreset' ? addToPresetOption : outputValue}
                                    isMulti={false}
                                    isSearchable={false}
                                    isClearable={true}
                                    options={[...targetPresetData.outputOptions, addToPresetOption]}
                                    isOptionDisabled={(option) =>
                                      option.slug !== field.value &&
                                      (targetSlugUsedCount.get(option.slug) ?? 0) > 0 &&
                                      option.slug !== 'addToPreset'
                                    }
                                    onChange={(option) => {
                                      field.onChange(option?.slug ?? null);
                                      handleOutputUpdate(outputNode, field.value, option?.slug ?? null);
                                    }}
                                    onBlur={field.onBlur}
                                  />
                                </div>
                                {_notNil(fieldState.error) && (
                                  <p className="red mt2 f6" data-test-element="error-message">
                                    {fieldState.error.message}
                                  </p>
                                )}
                              </>
                            );
                          } else {
                            const calcMeasurementValidation = validateDependencies(
                              outputNode.targetContainsSlug,
                              outputNode.dependencies ?? [],
                              targetPresetData,
                              formMethods
                            );
                            return (
                              <>
                                <div className="w-30">
                                  <Select
                                    name={outputNode.slug}
                                    className="w-100 pr2"
                                    value={
                                      !calcMeasurementValidation.isValid
                                        ? ''
                                        : outputValue?.slug === 'addToPreset'
                                          ? addToPresetOption
                                          : outputValue
                                    }
                                    isMulti={false}
                                    isSearchable={false}
                                    isClearable={true}
                                    disabled={!calcMeasurementValidation.isValid}
                                    options={[addToPresetOption]}
                                    isOptionDisabled={(option) =>
                                      option.slug !== field.value &&
                                      (targetSlugUsedCount.get(option.slug) ?? 0) > 0 &&
                                      option.slug !== 'addToPreset'
                                    }
                                    onChange={(option) => {
                                      field.onChange(option?.slug ?? null);
                                      handleOutputUpdate(outputNode, field.value, option?.slug ?? null);
                                    }}
                                    onBlur={field.onBlur}
                                  />
                                </div>
                                <div
                                  className="ba b--dashed pa3 ml5 mr2 w-60"
                                  data-testid={outputNode.slug + '-validation'}
                                >
                                  <ul className="list">
                                    <ListItem icon={outputNode.targetContainsSlug ? 'warning' : 'success'}>
                                      <p className="near-black lh-copy v-mid f6">
                                        {outputNode.targetContainsSlug
                                          ? targetContainsSlugMessage
                                          : targetDoesNotContainsSlugMessage}
                                      </p>
                                    </ListItem>
                                    <p className="pb3 f6">Dependent measurements required to add to preset:</p>
                                    {Array.from(outputNode.dependencies?.values() ?? []).map((dependency) => {
                                      return (
                                        <div className="pl3" key={dependency}>
                                          <ListItemDependency
                                            dependentValidation={
                                              calcMeasurementValidation?.dependenciesValidation[dependency] ?? undefined
                                            }
                                            dependency={dependency}
                                          />
                                        </div>
                                      );
                                    })}
                                  </ul>
                                </div>
                              </>
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                  {outputNode.children.size === 0 ? null : (
                    <div className="pt1">
                      {Array.from(outputNode.children.values()).map((inputNode) => (
                        <div
                          key={inputNode.slug}
                          className="flex items-center mt2"
                          data-test-element="input-container"
                          data-test-key={inputNode.slug}
                        >
                          <label className="w-30 pl4">{inputNode.label}</label>
                          <div className="flex w-70" data-test-element="input-target-slug">
                            <Controller
                              control={formMethods.control}
                              name={inputNode.slug}
                              render={({ field, fieldState }) => {
                                const targetOutputSlug = formMethods.getValues(outputNode.slug) ?? '';
                                const value =
                                  field.value === 'dropMeasurement'
                                    ? dropMeasurementOption
                                    : (targetPresetData.options
                                        .get(targetOutputSlug)
                                        ?.children?.get(field.value ?? '') ?? null);
                                const options = Array.from(
                                  targetPresetData.options.get(targetOutputSlug)?.children?.values() ?? []
                                );
                                return (
                                  <>
                                    <Select
                                      className="w-30 pr2"
                                      value={value?.slug === 'dropMeasurement' ? dropMeasurementOption : value}
                                      isMulti={false}
                                      isSearchable={false}
                                      disabled={targetOutputSlug === 'addToPreset' || _isEmptyString(targetOutputSlug)}
                                      isClearable={true}
                                      options={[...options, dropMeasurementOption]}
                                      isOptionDisabled={(option) =>
                                        option.slug !== field.value &&
                                        (targetSlugUsedCount.get(option.slug) ?? 0) > 0 &&
                                        option.slug !== 'dropMeasurement'
                                      }
                                      onChange={(option) => {
                                        field.onChange(option?.slug ?? null);
                                      }}
                                      onBlur={field.onBlur}
                                    />
                                    {_notNil(fieldState.error) && (
                                      <p className="red mt2 f6" data-test-element="error-message">
                                        {fieldState.error.message}
                                      </p>
                                    )}
                                  </>
                                );
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="pt3 b--moon-gray">
            <Button
              submit
              testKey="createDataTableFromTemplateStepFormSubmit"
              testId="map-measurements-submit"
              disabled={!formMethods.formState.isValid}
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

const PresetHeader: FC<{ preset: StudySettingsV1; label: string; rowWidth: number }> = ({
  preset,
  label,
  rowWidth,
}) => (
  <div className={`flex flex-column f6 w-${rowWidth}`}>
    <p>{label}</p>
    <p className="near-black">{preset?.title ?? '- No Preset -'}</p>
  </div>
);

export const PresetMapping: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    response: activeVariablesResponse,
    loading: activeVariablesLoading,
    invoke: loadActiveVariables,
  } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/active-variables',
    path: { studyApiId: 'sdy_invalid' },
    invokeOnInit: false,
  });

  const {
    response: cagesResponse,
    loading: cageLoading,
    invoke: loadCageList,
  } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.cages.list',
    path: { studyId: -1 },
    options: { onError: { toast: true, slug: 'study-cage-list' }, invokeOnInit: false },
  });

  useEffect(() => {
    if (_isNil(location.state)) {
      errorToast('Could not initialize move animals form');
      navigate(
        RouteService.web({
          route: 'dashboard',
        }).route
      );
    } else {
      loadCageList({ path: { studyId: location.state.targetStudy.id } });
      loadActiveVariables({
        path: { studyApiId: location.state.sourceStudy.api_id },
        body: { to_study_api_id: location.state.targetStudy.api_id, animal_api_ids: location.state.animalApiIds },
      });
    }
  }, [location?.state]);

  const loading =
    activeVariablesLoading || cageLoading || [activeVariablesResponse, cagesResponse, location.state].some(_isNil);

  if (loading || _isNil(location.state)) {
    return <Loading txt="Loading move animals form" />;
  }
  const activeVariables = activeVariablesResponse?.body as ActiveVariablesResponseV1;
  const targetCages = cagesResponse?.body as { data: Cage[] };
  const targetContainsMatchingCageNames = targetCages?.data.some((cage) =>
    location.state.animalCageNames.has(cage.name)
  );

  return (
    <PresetMappingActiveVariables
      sourceStudy={location.state.sourceStudy}
      targetStudy={location.state.targetStudy}
      sourceSettings={activeVariables.from_settings}
      targetSettings={activeVariables.to_settings ?? {}}
      animalApiIds={location.state.animalApiIds}
      duplicateCageNames={targetContainsMatchingCageNames}
    />
  );
};
